import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  BookOpen,
  Headphones,
  Eye,
  Calendar,
  Camera,
  Info,
  QrCode,

} from 'lucide-react';
import locationsData from '../data/locations.json';
import muzeData from '../data/muze-db.json';
import MediaPlayer from '../components/MediaPlayer';
import Modal from '../components/Modal';
import QRScanner from '../components/QRScanner';
import NoImage from '../components/NoImage';
import LanguageSelector from '../components/LanguageSelector';
import { getStoredLanguage, normalizeLanguage, setAppLanguage } from '../utils/language';

const LocationDetail = () => {
  const { id, muzeCode, sectionNo } = useParams();
  const navigate = useNavigate();3

  // Sayfa yüklendiğinde scroll pozisyonunu sıfırla
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Eski location ID ile gelen istekleri müze koduna yönlendir
  useEffect(() => {
    if (id && !muzeCode) {
      // Müze kodları mapping (ters mapping)
      const reverseMuzeCodeMapping = {
        1: 'km', // Kent Müzesi
        2: 'oa', // Onur Anıtı
        3: 'ti', // Tütün İskelesi
        4: 'ac', // Alaçam Mübadele Müzesi
        5: 'az', // Amazon Köyü
        6: 'sm', // Samsun Müzesi
        7: 'st', // Sadi Tekkesi
        8: 'ia', // İlkadım Anıtı
        9: 'gz', // Gazi Müzesi
        10: 'hv', // Havza Atatürk Evi
        11: 'bv', // Bandırma Vapuru
        12: 'bf'  // Bafra Tütün Müzesi
      };

      const locationId = parseInt(id);
      const muzeCode = reverseMuzeCodeMapping[locationId];
      
      if (muzeCode) {
        // Müze ise müze URL'sine yönlendir
        navigate(`/muze/${muzeCode}/1`, { replace: true });
        return;
      } else {
        // Müze değilse ana sayfaya yönlendir
        navigate('/', { replace: true });
        return;
      }
    }
  }, [id, muzeCode, navigate]);

  // Müze kodları mapping
  const muzeCodeMapping = {
    'km': 1, // Kent Müzesi location ID'si
    'oa': 2, // Onur Anıtı location ID'si
    'ti': 3, // Tütün İskelesi location ID'si
    'ac': 4, // Alaçam Mübadele Müzesi location ID'si
    'az': 5, // Amazon Köyü location ID'si
    'sm': 6, // Samsun Müzesi location ID'si
    'st': 7, // Sadi Tekkesi location ID'si
    'ia': 8, // İlkadım Anıtı location ID'si
    'gz': 9, // Gazi Müzesi location ID'si
    'hv': 10, // Havza Atatürk Evi location ID'si
    'bv': 11, // Bandırma Vapuru location ID'si
    'bf': 12  // Bafra Tütün Müzesi location ID'si
  };

  // Image alanını diziye normalize eden yardımcı fonksiyon
  const normalizeImages = (imageField) => {
    if (Array.isArray(imageField)) return imageField.filter(Boolean);
    if (typeof imageField === 'string' && imageField.trim() !== '') {
      return [imageField];
    }
    return [];
  };

  // Müze verilerini muze-db.json'dan yükle ve locations.json formatına dönüştür
  const buildLocationFromMuzeData = (muzeCode) => {
    const locationId = muzeCodeMapping[muzeCode.toLowerCase()];
    const baseLocation = locationsData.locations.find(loc => loc.id === locationId);

    if (!baseLocation) return null;

    // Müze koduna göre tüm bölümleri al
    const allSections = muzeData.sectionList
      .filter(section => section.muzeadi === muzeCode.toLowerCase());

    // Kent Müzesi için özel hiyerarşik yapı (3 haneli ana, 4 haneli alt)
    if (muzeCode.toLowerCase() === 'km') {
      // Ana bölümleri (3 haneli) ve alt bölümleri (4 haneli) ayır
      const mainSections = allSections.filter(section => section.no.length === 3);
      const subSections = allSections.filter(section => section.no.length === 4);

      // Ana bölümleri oluştur ve alt bölümlerini ekle
      const sections = mainSections.map(mainSection => {
        // Bu ana bölüme ait alt bölümleri bul (örn: 108 -> 1081, 1082, 1083, 1084)
        const relatedSubsections = subSections
          .filter(subSection => subSection.no.startsWith(mainSection.no))
          .map(subSection => {
            const baseAudio = subSection.src;
            const audioEng = subSection.src_eng;
            const audioRu =
              subSection.src_ru ||
              (baseAudio && baseAudio.includes('/media/sound/tr/')
                ? baseAudio.replace('/media/sound/tr/', '/media/sound/ru/')
                : undefined);

            return {
              id: `subsection-${subSection.muzeadi}-${subSection.no}`,
              title: subSection.name,
              titleEng: subSection.name_eng,
              titleRu: subSection.name_ru,
              description: subSection.text?.substring(0, 200) + '...' || 'Açıklama mevcut değil',
              descriptionEng: subSection.text_eng ? (subSection.text_eng.substring(0, 200) + '...') : undefined,
              descriptionRu: subSection.text_ru ? (subSection.text_ru.substring(0, 200) + '...') : undefined,
              qrCode: `${subSection.muzeadi.toUpperCase()}_${subSection.no}`,
              media: {
                audio: baseAudio,
                audioEng,
                audioRu,
                images: normalizeImages(subSection.image)
              },
              content: {
                text: subSection.text,
                textEng: subSection.text_eng,
                textRu: subSection.text_ru
              }
            };
          });

        const baseAudio = mainSection.src;
        const audioEng = mainSection.src_eng;
        const audioRu =
          mainSection.src_ru ||
          (baseAudio && baseAudio.includes('/media/sound/tr/')
            ? baseAudio.replace('/media/sound/tr/', '/media/sound/ru/')
            : undefined);

        return {
          id: `section-${mainSection.muzeadi}-${mainSection.no}`,
          title: mainSection.name,
          titleEng: mainSection.name_eng,
          titleRu: mainSection.name_ru,
          description: mainSection.text?.substring(0, 200) + '...' || 'Açıklama mevcut değil',
          descriptionEng: mainSection.text_eng ? (mainSection.text_eng.substring(0, 200) + '...') : undefined,
          descriptionRu: mainSection.text_ru ? (mainSection.text_ru.substring(0, 200) + '...') : undefined,
          qrCode: `${mainSection.muzeadi.toUpperCase()}_${mainSection.no}`,
          media: {
            audio: baseAudio,
            audioEng,
            audioRu,
            images: normalizeImages(mainSection.image)
          },
          content: {
            text: mainSection.text,
            textEng: mainSection.text_eng,
            textRu: mainSection.text_ru
          },
          subsections: relatedSubsections
        };
      });

      const titleEng = baseLocation.titleEng || baseLocation.title;
      const titleRu = baseLocation.titleRu || baseLocation.titleEng || baseLocation.title;

      return {
        ...baseLocation,
        titleRu,
        titleEng,
        sections
      };
    } else {
      // Diğer müzeler için düz yapı (tüm bölümler ana bölüm)
      const sections = allSections.map(section => {
        const baseAudio = section.src;
        const audioEng = section.src_eng;
        const audioRu =
          section.src_ru ||
          (baseAudio && baseAudio.includes('/media/sound/tr/')
            ? baseAudio.replace('/media/sound/tr/', '/media/sound/ru/')
            : undefined);

        return {
          id: `section-${section.muzeadi}-${section.no}`,
          title: section.name,
          titleEng: section.name_eng,
          titleRu: section.name_ru,
          description: section.text?.substring(0, 200) + '...' || 'Açıklama mevcut değil',
          descriptionEng: section.text_eng ? (section.text_eng.substring(0, 200) + '...') : undefined,
          descriptionRu: section.text_ru ? (section.text_ru.substring(0, 200) + '...') : undefined,
          qrCode: `${section.muzeadi.toUpperCase()}_${section.no}`,
          media: {
            audio: baseAudio,
            audioEng,
            audioRu,
            images: normalizeImages(section.image)
          },
          content: {
            text: section.text,
            textEng: section.text_eng,
            textRu: section.text_ru
          },
          subsections: []
        };
      });

      const titleEng = baseLocation.titleEng || baseLocation.title;
      const titleRu = baseLocation.titleRu || baseLocation.titleEng || baseLocation.title;

      return {
        ...baseLocation,
        titleRu,
        titleEng,
        sections
      };
    }
  };

  // Location'ı bul
  let location;
  let initialSectionIndex = 0;
  let initialSubsectionIndex = null;

  if (muzeCode && sectionNo) {
    // Müze kodu ile geliyorsa - muze-db.json'dan veri yükle
    location = buildLocationFromMuzeData(muzeCode);

    if (location && location.sections) {
      // Bölüm numarasına göre section'ı bul
      const sectionIndex = location.sections.findIndex(section =>
        section.qrCode === `${muzeCode.toUpperCase()}_${sectionNo}`
      );

      if (sectionIndex >= 0) {
        initialSectionIndex = sectionIndex;
      } else {
        // Alt bölüm olabilir, ana bölümü bul
        for (let i = 0; i < location.sections.length; i++) {
          const section = location.sections[i];
          if (section.subsections) {
            const subIndex = section.subsections.findIndex(sub =>
              sub.qrCode === `${muzeCode.toUpperCase()}_${sectionNo}`
            );
            if (subIndex >= 0) {
              initialSectionIndex = i;
              initialSubsectionIndex = subIndex;
              break;
            }
          }
        }
      }
    }
  }

  // State management
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(initialSectionIndex);
  const [currentSubsectionIndex, setCurrentSubsectionIndex] = useState(initialSubsectionIndex);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [isMediaPlayerOpen, setIsMediaPlayerOpen] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);

  const [currentLanguage, setCurrentLanguage] = useState(getStoredLanguage());
  const t = (tr, en, ru) => {
    if (currentLanguage === 'EN') return en;
    if (currentLanguage === 'RU') return ru || en || tr;
    return tr;
  };
  const getDisplayTitle = (item) => {
    if (!item) return '';
    if (currentLanguage === 'RU') return item.titleRu || item.titleEng || item.title;
    if (currentLanguage === 'EN') return item.titleEng || item.title;
    return item.title;
  };
  const getDisplayDescription = (item) => {
    if (!item) return '';
    if (currentLanguage === 'RU') return item.descriptionRu || item.descriptionEng || item.description;
    if (currentLanguage === 'EN') return item.descriptionEng || item.description;
    return item.description;
  };
  const getDisplayText = (item) => {
    if (!item) return '';
    const content = item.content || {};
    if (currentLanguage === 'RU') return content.textRu || content.textEng || content.text || item.descriptionRu || item.descriptionEng || item.description;
    if (currentLanguage === 'EN') return content.textEng || content.text || item.descriptionEng || item.description;
    return content.text || item.description;
  };
  const getMediaUrl = (media) => {
    if (!media) return null;
    if (currentLanguage === 'RU') return media.audioRu || media.audioEng || media.audio;
    if (currentLanguage === 'EN') return media.audioEng || media.audio || media.audioRu;
    return media.audio || media.audioEng || media.audioRu;
  };

  // Initialize language from localStorage and listen for changes
  useEffect(() => {
    try {
      setCurrentLanguage(getStoredLanguage());
    } catch (_) {}

    const handler = (e) => {
      const next = normalizeLanguage(e?.detail?.language);
      setCurrentLanguage(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('app-language-change', handler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('app-language-change', handler);
      }
    };
  }, []);

  // When language changes, update currentAudio title and url to reflect selected language
  useEffect(() => {
    if (!isMediaPlayerOpen || !currentAudio) return;
    const section = location?.sections?.[currentSectionIndex];
    const sub = currentSubsectionIndex !== null ? section?.subsections?.[currentSubsectionIndex] : null;
    const newTitle = sub ? getDisplayTitle(sub) : getDisplayTitle(section);
    const newUrl = sub ? getMediaUrl(sub?.media) : getMediaUrl(section?.media);
    if ((newTitle && currentAudio.title !== newTitle) || (newUrl && currentAudio.url !== newUrl)) {
      setCurrentAudio({ ...currentAudio, title: newTitle || currentAudio.title, url: newUrl || currentAudio.url });
    }
  }, [currentLanguage, isMediaPlayerOpen, currentAudio, currentSectionIndex, currentSubsectionIndex, location]);
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [heroImageError, setHeroImageError] = useState(false);
  const [locationImageError, setLocationImageError] = useState(false);

  // Bölüm veya alt bölüm değiştiğinde ana görsel hata durumunu sıfırla
  useEffect(() => {
    setHeroImageError(false);
    setCurrentImageIndex(0);
  }, [currentSectionIndex, currentSubsectionIndex]);

  // Media player navigation functions
  const handlePreviousSection = () => {
    if (currentSubsectionIndex !== null) {
      // Alt bölümdeyiz, önceki alt bölüme geç
      if (currentSubsectionIndex > 0) {
        const newSubIndex = currentSubsectionIndex - 1;
        setCurrentSubsectionIndex(newSubIndex);

        // Audio'yu güncelle
        const newSubsection = selectedSection.subsections[newSubIndex];
        if (newSubsection?.media?.audio || newSubsection?.media?.audioEng || newSubsection?.media?.audioRu) {
          setCurrentAudio({
            url: getMediaUrl(newSubsection.media),
            title: getDisplayTitle(newSubsection)
          });
        }
      } else {
        // İlk alt bölümdeyiz, ana bölüme geç
        setCurrentSubsectionIndex(null);
        if (selectedSection?.media?.audio || selectedSection?.media?.audioEng || selectedSection?.media?.audioRu) {
          setCurrentAudio({
            url: getMediaUrl(selectedSection.media),
            title: getDisplayTitle(selectedSection)
          });
        }
      }
    } else {
      // Ana bölümdeyiz, önceki ana bölüme geç
      if (currentSectionIndex > 0) {
        const newIndex = currentSectionIndex - 1;
        setCurrentSectionIndex(newIndex);
        setCurrentSubsectionIndex(null);

        const newSection = location.sections[newIndex];
        if (newSection?.media?.audio || newSection?.media?.audioEng || newSection?.media?.audioRu) {
          setCurrentAudio({
            url: getMediaUrl(newSection.media),
            title: getDisplayTitle(newSection)
          });
        }
      }
    }
  };

  const handleNextSection = () => {
    if (currentSubsectionIndex !== null) {
      // Alt bölümdeyiz
      if (currentSubsectionIndex < selectedSection.subsections.length - 1) {
        // Sonraki alt bölüme geç
        const newSubIndex = currentSubsectionIndex + 1;
      setCurrentSubsectionIndex(newSubIndex);

      const newSubsection = selectedSection.subsections[newSubIndex];
      if (newSubsection?.media?.audio || newSubsection?.media?.audioEng || newSubsection?.media?.audioRu) {
        setCurrentAudio({
          url: getMediaUrl(newSubsection.media),
          title: getDisplayTitle(newSubsection)
        });
      }
      } else {
        // Son alt bölümdeyiz, sonraki ana bölüme geç
        if (currentSectionIndex < location.sections.length - 1) {
          const newIndex = currentSectionIndex + 1;
          setCurrentSectionIndex(newIndex);
          setCurrentSubsectionIndex(null);

          const newSection = location.sections[newIndex];
          if (newSection?.media?.audio || newSection?.media?.audioEng) {
            setCurrentAudio({
              url: currentLanguage === 'EN' ? (newSection.media.audioEng || newSection.media.audio) : newSection.media.audio,
              title: currentLanguage === 'EN' ? (newSection.titleEng || newSection.title) : newSection.title
            });
          }
        }
      }
    } else {
      // Ana bölümdeyiz
      if (selectedSection.subsections && selectedSection.subsections.length > 0) {
        // Alt bölümler varsa ilk alt bölüme geç
        setCurrentSubsectionIndex(0);

        const firstSubsection = selectedSection.subsections[0];
        if (firstSubsection?.media?.audio || firstSubsection?.media?.audioEng) {
          setCurrentAudio({
            url: currentLanguage === 'EN' ? (firstSubsection.media.audioEng || firstSubsection.media.audio) : firstSubsection.media.audio,
            title: currentLanguage === 'EN' ? (firstSubsection.titleEng || firstSubsection.title) : firstSubsection.title
          });
        }
      } else {
        // Alt bölüm yoksa sonraki ana bölüme geç
        if (currentSectionIndex < location.sections.length - 1) {
          const newIndex = currentSectionIndex + 1;
          setCurrentSectionIndex(newIndex);

          const newSection = location.sections[newIndex];
          if (newSection?.media?.audio || newSection?.media?.audioEng) {
            setCurrentAudio({
              url: currentLanguage === 'EN' ? (newSection.media.audioEng || newSection.media.audio) : newSection.media.audio,
              title: currentLanguage === 'EN' ? (newSection.titleEng || newSection.title) : newSection.title
            });
          }
        }
      }
    }
  };

  // Navigation durumlarını kontrol et
  const hasPrevious = () => {
    if (currentSubsectionIndex !== null) {
      return currentSubsectionIndex > 0 || currentSectionIndex > 0;
    }
    return currentSectionIndex > 0;
  };

  const hasNext = () => {
    if (currentSubsectionIndex !== null) {
      return currentSubsectionIndex < selectedSection.subsections.length - 1 ||
             currentSectionIndex < location.sections.length - 1;
    }
    return (selectedSection.subsections && selectedSection.subsections.length > 0) ||
           currentSectionIndex < location.sections.length - 1;
  };

  // Get current section and subsection
  const selectedSection = location?.sections?.[currentSectionIndex];
  const currentSubsection = currentSubsectionIndex !== null ? selectedSection?.subsections?.[currentSubsectionIndex] : null;





  // Navigation functions
  const handleSectionSelect = (section, index) => {
    setCurrentSectionIndex(index);
    setCurrentSubsectionIndex(null);
    setIsSidebarOpen(false);

    // Media player açıksa audio'yu güncelle
    if (isMediaPlayerOpen && section?.media?.audio) {
      setCurrentAudio({
        url: currentLanguage === 'EN' ? (section.media.audioEng || section.media.audio) : section.media.audio,
        title: currentLanguage === 'EN' ? (section.titleEng || section.title) : section.title
      });
    }
  };

  const handleSubsectionSelect = (subsection, subsectionIndex, sectionIndex) => {
    // Ana bölümü de güncelle
    setCurrentSectionIndex(sectionIndex);
    setCurrentSubsectionIndex(subsectionIndex);
    setIsSidebarOpen(false);

    // Media player açıksa audio'yu güncelle
    if (isMediaPlayerOpen && subsection?.media?.audio) {
      setCurrentAudio({
        url: currentLanguage === 'EN' ? (subsection.media.audioEng || subsection.media.audio) : subsection.media.audio,
        title: currentLanguage === 'EN' ? (subsection.titleEng || subsection.title) : subsection.title
      });
    }
  };

  // QR Scanner handlers
  const handleQRScan = () => {
    setIsQRScannerOpen(true);
  };

  const handleQRScanResult = (result) => {
    console.log('QR kod tarandı:', result);

    // QR kod formatını kontrol et ve uygun URL'ye yönlendir
    if (result) {      
      // Tam link formatında QR kod kontrolü
      if (result.startsWith('https://')) {
        console.log('Tam link QR kodu tespit edildi:', result);
        try {
          const url = new URL(result);
          const path = url.pathname;
          console.log('Yönlendirilecek path:', path);
          setIsSidebarOpen(false); // Sidebar'ı kapat
          window.location.href = path; // Direkt path'e yönlendir
          setIsQRScannerOpen(false);
          return;
        } catch (error) {
          console.error('URL parse hatası:', error);
        }
      }

      // Path formatında QR kod kontrolü (/muze/gz/1 gibi)
      if (result.startsWith('/muze/')) {
        console.log('Path formatında QR kodu tespit edildi:', result);
        setIsSidebarOpen(false); // Sidebar'ı kapat
        window.location.href = result; // Direkt path'e yönlendir
        setIsQRScannerOpen(false);
        return;
      }

      // Eski format QR kodları için uyumluluk (KM_101 gibi)
      const muzeCodeMatch = result.match(/^([A-Z]{1,2})_(\d+)$/);
      if (muzeCodeMatch) {
        const [, muzeCodeUpper, sectionNo] = muzeCodeMatch;
        const muzeCodeLower = muzeCodeUpper.toLowerCase();

        console.log('Müze kodu algılandı:', muzeCodeLower, sectionNo);

        // Direkt URL'ye yönlendir
        setIsSidebarOpen(false); // Sidebar'ı kapat
        window.location.href = `/muze/${muzeCodeLower}/${sectionNo}`;
        setIsQRScannerOpen(false);
        return;
      }

      // Tüm müzelerde QR kodu ara
      const foundLocation = locationsData.locations.find(loc => {
        if (loc.sections) {
          for (const section of loc.sections) {
            if (section.qrCode === result) return true;
            if (section.subsections) {
              for (const subsection of section.subsections) {
                if (subsection.qrCode === result) return true;
              }
            }
          }
        }
        return false;
      });

      if (foundLocation) {
        // Müze kodları mapping
        const muzeCodeMapping = {
          1: 'km', // Kent Müzesi
          2: 'oa', // Onur Anıtı
          3: 'ti', // Tütün İskelesi
          4: 'ac', // Alaçam Mübadele Müzesi
          5: 'az', // Amazon Köyü
          6: 'sm', // Samsun Müzesi
          7: 'st', // Sadi Tekkesi
          8: 'ia', // İlkadım Anıtı
          9: 'gz', // Gazi Müzesi
          10: 'hv', // Havza Atatürk Evi
          11: 'bv', // Bandırma Vapuru
          12: 'bf'  // Bafra Tütün Müzesi
        };

        // Müze kodunu bul
        const muzeCode = muzeCodeMapping[foundLocation.id];
        
        if (muzeCode) {
          // QR kodundan bölüm numarasını çıkar
          const sectionMatch = result.match(/^[A-Z]{1,2}_(\d+)$/);
          if (sectionMatch) {
            const sectionNumber = sectionMatch[1];
            setIsSidebarOpen(false); // Sidebar'ı kapat
            window.location.href = `/muze/${muzeCode}/${sectionNumber}`;
          } else {
            // Bölüm numarası bulunamazsa ilk bölüme git
            setIsSidebarOpen(false); // Sidebar'ı kapat
            window.location.href = `/muze/${muzeCode}/1`;
          }
        } else {
          // Müze değilse ana sayfaya yönlendir
          setIsSidebarOpen(false); // Sidebar'ı kapat
          window.location.href = '/';
        }
        setIsQRScannerOpen(false);
        return;
      }
    }

    alert('Bu QR kod tanınmadı: ' + result);
    setIsQRScannerOpen(false);
  };

  const goToPreviousSection = () => {
    // Sayfayı en üste kaydır
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (currentSubsectionIndex !== null) {
      // Alt bölümdeyse, önceki alt bölüme git
      if (currentSubsectionIndex > 0) {
        const newSubIndex = currentSubsectionIndex - 1;
        setCurrentSubsectionIndex(newSubIndex);

        // Media player açıksa audio'yu güncelle
        if (isMediaPlayerOpen) {
          const newSubsection = selectedSection.subsections[newSubIndex];
          if (newSubsection?.media?.audio || newSubsection?.media?.audioEng || newSubsection?.media?.audioRu) {
            setCurrentAudio({
              url: getMediaUrl(newSubsection.media),
              title: getDisplayTitle(newSubsection)
            });
          }
        }
      } else {
        // İlk alt bölümdeyse, ana bölüme dön
        setCurrentSubsectionIndex(null);

        // Media player açıksa audio'yu güncelle
        if (isMediaPlayerOpen && selectedSection?.media?.audio) {
          setCurrentAudio({
            url: currentLanguage === 'EN' ? (selectedSection.media.audioEng || selectedSection.media.audio) : selectedSection.media.audio,
            title: selectedSection.title
          });
        }
      }
    } else if (currentSectionIndex > 0) {
      // Ana bölümdeyse, önceki bölüme git
      const previousSectionIndex = currentSectionIndex - 1;
      const previousSection = location.sections[previousSectionIndex];

      setCurrentSectionIndex(previousSectionIndex);

      // Önceki bölümün alt bölümleri varsa, son alt bölüme git
      if (previousSection.subsections && previousSection.subsections.length > 0) {
        const lastSubIndex = previousSection.subsections.length - 1;
        setCurrentSubsectionIndex(lastSubIndex);

        // Media player açıksa audio'yu güncelle
        if (isMediaPlayerOpen) {
          const lastSubsection = previousSection.subsections[lastSubIndex];
          if (lastSubsection?.media?.audio || lastSubsection?.media?.audioEng || lastSubsection?.media?.audioRu) {
            setCurrentAudio({
              url: getMediaUrl(lastSubsection.media),
              title: getDisplayTitle(lastSubsection)
            });
          }
        }
      } else {
        // Alt bölüm yoksa ana bölümde kal
        setCurrentSubsectionIndex(null);

        // Media player açıksa audio'yu güncelle
        if (isMediaPlayerOpen && (previousSection?.media?.audio || previousSection?.media?.audioEng || previousSection?.media?.audioRu)) {
          setCurrentAudio({
            url: getMediaUrl(previousSection.media),
            title: getDisplayTitle(previousSection)
          });
        }
      }
    }
  };

  const goToNextSection = () => {
    // Sayfayı en üste kaydır
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    const currentSection = location.sections[currentSectionIndex];

    if (currentSubsectionIndex === null && currentSection.subsections && currentSection.subsections.length > 0) {
      // Ana bölümdeyse ve alt bölümler varsa, ilk alt bölüme git
      setCurrentSubsectionIndex(0);

      // Media player açıksa audio'yu güncelle
        if (isMediaPlayerOpen) {
          const firstSubsection = currentSection.subsections[0];
          if (firstSubsection?.media?.audio || firstSubsection?.media?.audioEng || firstSubsection?.media?.audioRu) {
            setCurrentAudio({
              url: getMediaUrl(firstSubsection.media),
              title: getDisplayTitle(firstSubsection)
            });
          }
        }
    } else if (currentSubsectionIndex !== null && currentSubsectionIndex < currentSection.subsections.length - 1) {
      // Alt bölümdeyse ve daha alt bölüm varsa, sonraki alt bölüme git
      const newSubIndex = currentSubsectionIndex + 1;
      setCurrentSubsectionIndex(newSubIndex);

      // Media player açıksa audio'yu güncelle
        if (isMediaPlayerOpen) {
          const newSubsection = currentSection.subsections[newSubIndex];
          if (newSubsection?.media?.audio || newSubsection?.media?.audioEng || newSubsection?.media?.audioRu) {
            setCurrentAudio({
              url: getMediaUrl(newSubsection.media),
              title: getDisplayTitle(newSubsection)
            });
          }
        }
    } else if (currentSectionIndex < location.sections.length - 1) {
      // Son alt bölümdeyse veya alt bölüm yoksa, sonraki ana bölüme git
      const newSectionIndex = currentSectionIndex + 1;
      setCurrentSectionIndex(newSectionIndex);
      setCurrentSubsectionIndex(null);

      // Media player açıksa audio'yu güncelle
        if (isMediaPlayerOpen) {
          const newSection = location.sections[newSectionIndex];
          if (newSection?.media?.audio || newSection?.media?.audioEng || newSection?.media?.audioRu) {
            setCurrentAudio({
              url: getMediaUrl(newSection.media),
              title: getDisplayTitle(newSection)
            });
          }
        }
    }
  };

  const handleLanguageChange = (lang) => {
    const next = setAppLanguage(lang);
    setCurrentLanguage(next);
  };

  if (!location) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mx-auto"></div>
          <p className="mt-6 text-primary-700 font-medium">
            {id ? 'Müze bilgileri bulunamadı' : 'Müze bilgileri yükleniyor...'}
          </p>
          {id && (
            <button
              onClick={() => navigate('/')}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Ana Sayfaya Dön
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" style={{ minHeight: '100vh' }}>
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="px-4 sm:px-6">
          <div className="flex items-center h-16">
            {/* Left: Back */}
            <button
              onClick={() => navigate('/')}
              className="flex-shrink-0 p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            {/* Center: Title + actions (mobile centered, desktop left-aligned) */}
            <div className="flex-1 flex flex-col items-center px-2 min-w-0 lg:items-start">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate text-center lg:text-left">
                {getDisplayTitle(location)}
              </h1>
              <div className="mt-1 flex items-center gap-2 lg:hidden">
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="inline-flex items-center px-2 py-1 text-xs sm:text-sm font-semibold rounded bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors duration-200"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <BookOpen className="w-4 h-4 mr-1" />
                  {t('Bölümler', 'Sections', 'Разделы')}
                </button>
                {(currentSubsection?.qrCode || selectedSection?.qrCode) && (
                  <button
                    onClick={handleQRScan}
                    className="inline-flex items-center px-2 py-1 text-xs sm:text-sm font-semibold rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors duration-200"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <QrCode className="w-4 h-4 mr-1" />
                    QR
                  </button>
                )}
              </div>
              <p className="hidden lg:block text-xs sm:text-sm text-gray-600 truncate mt-1">
                {currentSubsection
                  ? `${getDisplayTitle(selectedSection)} • ${getDisplayTitle(currentSubsection)}`
                  : (selectedSection ? getDisplayTitle(selectedSection) : location.category)}
              </p>
            </div>

            {/* Right: Language + menu (desktop) */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <LanguageSelector size="sm" labelMode="short" showIcon={false} onChange={handleLanguageChange} />
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="hidden lg:flex flex-shrink-0 p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
              >
                <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex min-h-screen pt-16">
        {/* Sidebar - Desktop */}
        <div className="hidden lg:flex lg:w-80 lg:flex-col bg-white shadow-xl border-r border-gray-200 fixed left-0 top-16 bottom-0 z-40">
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-primary-100">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold text-primary-800 flex items-center">
                  <BookOpen className="w-5 h-5 mr-2" />
                  {t('Müze Bölümleri', 'Museum Sections', 'Разделы музея')}
                </h2>
                <p className="text-sm text-primary-600 mt-1">
                  {t(
                    `${location.sections?.length || 0} bölüm keşfetmeyi bekliyor`,
                    `${location.sections?.length || 0} sections are waiting to be explored`,
                    `${location.sections?.length || 0} раздел(ов) ждут, чтобы их исследовали`
                  )}
                </p>
              </div>
              {(currentSubsection?.qrCode || selectedSection?.qrCode) && (
                <button
                  onClick={handleQRScan}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm shadow-sm flex-shrink-0"
                >
                  <QrCode className="w-4 h-4" />
                  <span>QR</span>
                </button>
              )}
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-2">
              {location.sections?.map((section, index) => (
                <div key={section.id} className="space-y-1">
                  <button
                    onClick={() => handleSectionSelect(section, index)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-200 group ${
                      currentSectionIndex === index && currentSubsectionIndex === null
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg transform scale-[1.02]'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-700 hover:shadow-md hover:transform hover:scale-[1.01]'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        currentSectionIndex === index && currentSubsectionIndex === null
                          ? 'bg-white/20'
                          : 'bg-primary-100'
                      }`}>
                        <Camera className={`w-5 h-5 ${
                          currentSectionIndex === index && currentSubsectionIndex === null
                            ? 'text-white'
                            : 'text-primary-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                      <h3 className="font-semibold">{getDisplayTitle(section)}</h3>
                        <p className={`text-sm mt-1 ${
                          currentSectionIndex === index && currentSubsectionIndex === null
                            ? 'text-white/80'
                            : 'text-gray-500'
                        }`}>
                          {(() => {
                            const desc = getDisplayDescription(section) || '';
                            return desc.length > 60 ? `${desc.substring(0, 60)}...` : desc;
                          })()}
                        </p>
                        {section.subsections && (
                          <div className={`text-xs mt-2 flex items-center ${
                            currentSectionIndex === index && currentSubsectionIndex === null
                              ? 'text-white/70'
                              : 'text-gray-400'
                          }`}>
                            <Eye className="w-3 h-3 mr-1" />
                            {section.subsections.length} {t('alt bölüm', 'subsections', 'подразделения')}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Alt bölümler - tüm bölümlerde göster */}
                  {section.subsections && (
                    <div className="ml-4 space-y-1">
                      {section.subsections.map((subsection, subIndex) => (
                        <button
                          key={subsection.id}
                          onClick={() => handleSubsectionSelect(subsection, subIndex, index)}
                          className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                            currentSectionIndex === index && currentSubsectionIndex === subIndex
                              ? 'bg-gradient-to-r from-primary-400 to-primary-500 text-white shadow-md'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <div className={`w-6 h-6 rounded-md flex items-center justify-center ${
                              currentSectionIndex === index && currentSubsectionIndex === subIndex
                                ? 'bg-white/20'
                                : 'bg-primary-100'
                            }`}>
                              <BookOpen className={`w-3 h-3 ${
                                currentSectionIndex === index && currentSubsectionIndex === subIndex
                                  ? 'text-white'
                                  : 'text-primary-600'
                              }`} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-sm">{getDisplayTitle(subsection)}</h4>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 lg:ml-80">
          {/* Content */}
          <div className="w-full">
            {selectedSection ? (
              <div className={`p-6 lg:p-8 max-w-4xl mx-auto ${isMediaPlayerOpen ? 'pb-44' : 'pb-32 lg:pb-20'}`}>
                {/* Hero Image */}
                <div className="mb-8">
                  <div className="relative h-64 sm:h-80 lg:h-96 rounded-2xl overflow-hidden shadow-2xl">
                    {(() => {
                      const images =
                        currentSubsection?.media?.images && currentSubsection.media.images.length > 0
                          ? currentSubsection.media.images
                          : selectedSection?.media?.images || [];

                      if (!heroImageError && images.length > 0) {
                        const safeIndex = Math.min(currentImageIndex, images.length - 1);
                        const imageSrc = images[safeIndex];

                        return (
                          <>
                            <img
                              src={imageSrc}
                              alt={getDisplayTitle(currentSubsection || selectedSection)}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={() => setHeroImageError(true)}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6 flex flex-col gap-3">
                              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1">
                                {getDisplayTitle(currentSubsection || selectedSection)}
                              </h1>

                              {/* Slider controls only if birden fazla görsel varsa */}
                              {images.length > 1 && (
                                <div className="flex items-center justify-between">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCurrentImageIndex((prev) =>
                                        prev === 0 ? images.length - 1 : prev - 1
                                      );
                                    }}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white text-sm"
                                    aria-label={t('Önceki görsel', 'Previous image', 'Предыдущее изображение')}
                                  >
                                    ‹
                                  </button>

                                  <div className="flex items-center gap-1.5">
                                    {images.map((_, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCurrentImageIndex(idx);
                                        }}
                                        className={`w-2.5 h-2.5 rounded-full transition-all ${
                                          idx === safeIndex
                                            ? 'bg-white shadow'
                                            : 'bg-white/40 hover:bg-white/70'
                                        }`}
                                        aria-label={`${idx + 1} / ${images.length}`}
                                      />
                                    ))}
                                  </div>

                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCurrentImageIndex((prev) =>
                                        prev === images.length - 1 ? 0 : prev + 1
                                      );
                                    }}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white text-sm"
                                    aria-label={t('Sonraki görsel', 'Next image', 'Следующее изображение')}
                                  >
                                    ›
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        );
                      }

                      return (
                        <div className="relative w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl">
                          <NoImage
                            className="w-full h-full rounded-2xl"
                            title={t('Bölüm Görseli', 'Section Image', 'Изображение раздела')}
                            description={t(
                              `${currentSubsection ? currentSubsection.title : selectedSection.title} için görsel bulunmuyor`,
                              `No image for ${getDisplayTitle(currentSubsection || selectedSection)}`,
                              `Нет изображения для ${getDisplayTitle(currentSubsection || selectedSection)}`
                            )}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent rounded-2xl" />
                          <div className="absolute bottom-6 left-6 right-6">
                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
                              {getDisplayTitle(currentSubsection || selectedSection)}
                            </h1>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Section Description - Full Content */}
                <div className="mb-8">
                  <div className="text-lg text-gray-700 leading-relaxed whitespace-pre-line text-justify">
                    {getDisplayText(currentSubsection || selectedSection)}
                  </div>
                </div>

                {/* Main Content */}
                {/* Bölüm Hakkında - Commented out for future use */}
                {/*
                <div className="prose prose-lg max-w-none mb-8">
                  <div className="bg-white rounded-2xl shadow-lg p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <BookOpen className="w-6 h-6 mr-3 text-primary-600" />
                      {currentSubsection ? 'Alt Bölüm Hakkında' : 'Bölüm Hakkında'}
                    </h2>

                    <div className="space-y-6 text-gray-700 leading-relaxed">
                      <p className="text-lg">
                        {currentSubsection ? (
                          `${currentSubsection.title} alt bölümünde ${selectedSection.title.toLowerCase()} ile ilgili özel detayları keşfedebilirsiniz. Bu alt bölüm, ana koleksiyonumuzun en önemli parçalarından birini oluşturmaktadır.`
                        ) : (
                          `Bu bölümde ${selectedSection.title.toLowerCase()} ile ilgili detaylı bilgileri bulabilirsiniz. Müzemizin en değerli koleksiyonlarından biri olan bu bölüm, ziyaretçilerimize tarihin derinliklerinde bir yolculuk sunmaktadır.`
                        )}
                      </p>

                      <p>
                        {currentSubsection ? (
                          `${currentSubsection.title} bölümü, özel arkeolojik bulgular ve nadir eserler barındırmaktadır. Bu eserler, döneminin sosyal, kültürel ve ekonomik yapısını anlamamıza yardımcı olan değerli ipuçları sunmaktadır.`
                        ) : (
                          'Arkeolojik bulgular ve etnografik eserler, geçmişin izlerini günümüze taşıyan sessiz tanıklar olarak karşımızda durmaktadır. Her bir eser, kendi döneminin kültürel zenginliğini ve yaşam tarzını yansıtmaktadır.'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                */}

                {/* Image Gallery - Commented out for future use */}
                {/*
                {selectedSection.media?.images && (
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <ImageIcon className="w-5 h-5 mr-2 text-primary-600" />
                      Galeri
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {selectedSection.media.images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => openImageModal(image, index)}
                          className="relative aspect-square rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-200 group"
                        >
                          <img
                            src={typeof image === 'string' ? image : `https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=400&fit=crop&auto=format`}
                            alt={typeof image === 'object' ? image.title : `Resim ${index + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
                          <div className="absolute bottom-2 left-2 right-2">
                            <p className="text-white text-xs font-medium bg-black/50 rounded px-2 py-1 line-clamp-2">
                              {image.title}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                */}

                {/* Subsections - sadece ana bölümde ve alt bölümler varsa göster */}
                {/* Alt Bölümler - Commented out for future use */}
                {/*
                {!currentSubsection && selectedSection.subsections && selectedSection.subsections.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <Eye className="w-5 h-5 mr-2 text-primary-600" />
                      Alt Bölümler
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      {selectedSection.subsections.map((subsection, subIndex) => (
                        <button
                          key={subsection.id}
                          onClick={() => handleSubsectionSelect(subsection, subIndex, currentSectionIndex)}
                          className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 p-6 text-left group hover:transform hover:scale-[1.02]"
                        >
                          <div>
                          <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                              {getDisplayTitle(subsection)}
                            </h4>
                            <p className="text-gray-600 text-sm line-clamp-2">
                              {getDisplayDescription(subsection)}
                            </p>
                            {subsection.qrCode && (
                              <div className="mt-3 flex items-center text-xs text-green-600">
                                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                                QR Kod: {subsection.qrCode}
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                */}

                {/* Alt bölüm detay içeriği */}
                {/* Alt Bölüm Detayları - Commented out and removed */}
                </div>
              ) : location?.sections && location.sections.length > 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('Bölüm Seçin', 'Select a Section', 'Выберите раздел')}</h3>
                  <p className="text-gray-500">{t('Keşfetmek için sol menüden bir bölüm seçin', 'Choose a section from the left menu to explore', 'Выберите раздел слева для просмотра')}</p>
                </div>
              </div>
            ) : (
              <div className="p-6 lg:p-8 max-w-4xl mx-auto pb-20 lg:pb-8">
                {/* Hero Image */}
                <div className="mb-8">
                  <div className="relative h-64 sm:h-80 lg:h-96 rounded-2xl overflow-hidden shadow-2xl">
                    {location?.image && !locationImageError ? (
                      <>
                        <img
                          src={location.image}
                          alt={location?.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={() => setLocationImageError(true)}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                        <div className="absolute bottom-6 left-6 right-6">
                          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">{currentLanguage === 'EN' ? (location?.titleEng || location?.title) : location?.title}</h1>
                          <p className="text-white/90 text-sm">{location?.category} • {location?.address}</p>
                        </div>
                      </>
                    ) : (
                      <div className="relative w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl">
                        <NoImage
                          className="w-full h-full rounded-2xl"
                          title={currentLanguage === 'EN' ? 'Location Image' : 'Lokasyon Görseli'}
                          description={currentLanguage === 'EN' ? `No image for ${location?.title}` : `${location?.title} için görsel bulunmuyor`}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent rounded-2xl" />
                        <div className="absolute bottom-6 left-6 right-6">
                          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">{currentLanguage === 'EN' ? (location?.titleEng || location?.title) : location?.title}</h1>
                          <p className="text-white/90 text-sm">{location?.category} • {location?.address}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Location Description */}
                <div className="mb-8">
                  <p className="text-lg text-gray-700 leading-relaxed text-justify">
                    {location?.description}
                  </p>
                </div>

                {/* Location Details */}
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-400 p-6 rounded-r-xl">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Info className="w-5 h-5 mr-2 text-blue-600" />
                      {currentLanguage === 'EN' ? 'Visitor Information' : 'Ziyaret Bilgileri'}
                    </h3>
                    <div className="space-y-2 text-sm">
                      {location?.openingHours && (
                        <p><strong>{currentLanguage === 'EN' ? 'Opening Hours' : 'Açılış Saatleri'}:</strong> {location.openingHours}</p>
                      )}
                      {location?.phone && (
                        <p><strong>{currentLanguage === 'EN' ? 'Phone' : 'Telefon'}:</strong> {location.phone}</p>
                      )}
                      {location?.website && (
                        <p><strong>{currentLanguage === 'EN' ? 'Website' : 'Website'}:</strong> <a href={location.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{location.website}</a></p>
                      )}
                    </div>
                  </div>

                  <div className="prose prose-lg max-w-none">
                    <p>
                      Bu lokasyon hakkında daha detaylı bilgi için lütfen doğrudan ziyaret edin veya iletişim bilgilerini kullanarak bilgi alın.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Navigation */}
          {selectedSection && (
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 sm:p-6 shadow-lg z-40">
              <div className="max-w-4xl mx-auto">
                {/* Main Navigation Bar */}
                <div className="flex items-center justify-between">
                  {/* Previous Button - Larger and more prominent */}
                  <button
                    onClick={goToPreviousSection}
                    disabled={currentSectionIndex === 0 && currentSubsectionIndex === null}
                    className={`group flex items-center justify-center gap-2 px-5 py-3 sm:px-6 sm:py-4 rounded-xl transition-colors duration-200 w-32 sm:w-40 ${
                      currentSectionIndex === 0 && currentSubsectionIndex === null
                        ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                        : 'text-primary-700 hover:text-primary-900 bg-gradient-to-r from-primary-50 to-primary-100 hover:from-primary-100 hover:to-primary-200 shadow-sm hover:shadow-lg border border-primary-200'
                    }`}
                  >
                    <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                    <span className="text-sm sm:text-base font-semibold whitespace-nowrap">
                      {t('Önceki', 'Previous', 'Предыдущий')}
                    </span>
                  </button>

                  {/* Progress Section - More spacious */}
                  <div className="flex flex-col items-center gap-3 flex-1 mx-6 sm:mx-8">
                    {/* Progress Bar */}
                    <div className="w-full max-w-md bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
                        style={{
                          width: `${((currentSectionIndex + 1) / location.sections.length) * 100}%`
                        }}
                      />
                    </div>
                    
                    {/* Page Info */}
                    <div className="flex items-center gap-2">
                                          <span className="text-xs sm:text-sm font-medium text-gray-700">
                      {currentSubsectionIndex !== null 
                        ? `${currentSectionIndex + 1}-${currentSubsectionIndex + 1}`
                        : `${currentSectionIndex + 1}`
                      }
                    </span>
                    <span className="text-gray-400">/</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-600">
                      {location.sections.length}
                    </span>
                    </div>
                  </div>

                  {/* Next Button - Larger and more prominent */}
                  <button
                    onClick={goToNextSection}
                    disabled={
                      // Sadece son bölümde ve son alt bölümde (veya alt bölüm yoksa) deaktif olsun
                      currentSectionIndex === (location?.sections?.length || 0) - 1 &&
                      (
                        (selectedSection?.subsections?.length > 0 && currentSubsectionIndex === (selectedSection?.subsections?.length || 0) - 1) ||
                        (!selectedSection?.subsections || selectedSection?.subsections?.length === 0)
                      )
                    }
                    className={`group flex items-center justify-center gap-2 px-5 py-3 sm:px-6 sm:py-4 rounded-xl transition-colors duration-200 w-32 sm:w-40 ${
                      currentSectionIndex === (location?.sections?.length || 0) - 1 &&
                      (
                        (selectedSection?.subsections?.length > 0 && currentSubsectionIndex === (selectedSection?.subsections?.length || 0) - 1) ||
                        (!selectedSection?.subsections || selectedSection?.subsections?.length === 0)
                      )
                        ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                        : 'text-primary-700 hover:text-primary-900 bg-gradient-to-r from-primary-50 to-primary-100 hover:from-primary-100 hover:to-primary-200 shadow-sm hover:shadow-lg border border-primary-200'
                    }`}
                  >
                    <span className="text-sm sm:text-base font-semibold whitespace-nowrap">
                      {t('Sonraki', 'Next', 'Следующий')}
                    </span>
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                  </button>
                </div>

                {/* Audio Button - Desktop only */}
                {((currentSubsection?.media?.audioEng || currentSubsection?.media?.audio) || (selectedSection?.media?.audioEng || selectedSection?.media?.audio)) && !isMediaPlayerOpen && (
                  <div className="hidden lg:flex justify-center pt-4">
                    <button
                      onClick={() => {
                        const audioUrl = currentLanguage === 'EN'
                          ? ((currentSubsection?.media?.audioEng || selectedSection?.media?.audioEng) || (currentSubsection?.media?.audio || selectedSection?.media?.audio))
                          : (currentSubsection?.media?.audio || selectedSection?.media?.audio);
                        const title = currentLanguage === 'EN'
                          ? ((currentSubsection?.titleEng || currentSubsection?.title) || (selectedSection?.titleEng || selectedSection?.title))
                          : ((currentSubsection?.title || selectedSection?.title));
                        setCurrentAudio({ url: audioUrl, title: title });
                        setIsMediaPlayerOpen(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200"
                    >
                      <Headphones className="w-4 h-4" />
                      <span className="text-sm font-medium">{t('Dinle', 'Listen', 'Слушать')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

             {/* Mobile Floating Audio Button */}
       {((currentSubsection?.media?.audioEng || currentSubsection?.media?.audio) || (selectedSection?.media?.audioEng || selectedSection?.media?.audio)) && !isMediaPlayerOpen && (
         <button
           onClick={() => {
            const audioUrl = currentLanguage === 'EN'
              ? ((currentSubsection?.media?.audioEng || selectedSection?.media?.audioEng) || (currentSubsection?.media?.audio || selectedSection?.media?.audio))
              : (currentSubsection?.media?.audio || selectedSection?.media?.audio);
            const title = currentLanguage === 'EN'
              ? ((currentSubsection?.titleEng || currentSubsection?.title) || (selectedSection?.titleEng || selectedSection?.title))
              : ((currentSubsection?.title || selectedSection?.title));
            setCurrentAudio({ url: audioUrl, title: title });
            setIsMediaPlayerOpen(true);
           }}
           className="lg:hidden fixed bottom-24 sm:bottom-28 right-4 sm:right-6 w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-200 flex items-center justify-center z-30"
         >
           <Headphones className="w-5 h-5" />
         </button>
       )}

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 max-w-[85vw] bg-white shadow-xl z-50 lg:hidden transform transition-transform duration-300 flex flex-col">
            <div className="p-4 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-primary-100 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base sm:text-lg font-bold text-primary-800 flex items-center min-w-0 flex-1">
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                  <span className="truncate">{currentLanguage === 'EN' ? 'Museum Sections' : 'Müze Bölümleri'}</span>
                </h2>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="flex-shrink-0 p-2 rounded-lg text-primary-600 hover:text-primary-800 hover:bg-primary-100 transition-colors duration-200 ml-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {(currentSubsection?.qrCode || selectedSection?.qrCode) && (
                <button
                  onClick={handleQRScan}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm shadow-sm"
                >
                  <QrCode className="w-4 h-4" />
                  <span>{t('QR Kod Tara', 'Scan QR', 'Сканировать QR')}</span>
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 min-h-0">
              <div className="space-y-2">
                {location.sections?.map((section, index) => (
                  <div key={section.id} className="space-y-1">
                    <button
                      onClick={() => handleSectionSelect(section, index)}
                      className={`w-full text-left p-4 rounded-xl transition-all duration-200 group ${
                        currentSectionIndex === index && currentSubsectionIndex === null
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg transform scale-[1.02]'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-700 hover:shadow-md hover:transform hover:scale-[1.01]'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          currentSectionIndex === index && currentSubsectionIndex === null
                            ? 'bg-white/20'
                            : 'bg-primary-100'
                        }`}>
                          <Camera className={`w-5 h-5 ${
                            currentSectionIndex === index && currentSubsectionIndex === null
                              ? 'text-white'
                              : 'text-primary-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold">{getDisplayTitle(section)}</h3>
                          <p className={`text-sm mt-1 ${
                            currentSectionIndex === index && currentSubsectionIndex === null
                              ? 'text-white/80'
                              : 'text-gray-500'
                          }`}>
                            {(() => {
                              const desc = getDisplayDescription(section) || '';
                              return desc.length > 60 ? `${desc.substring(0, 60)}...` : desc;
                            })()}
                          </p>
                        {section.subsections && (
                          <div className={`text-xs mt-2 flex items-center ${
                            currentSectionIndex === index && currentSubsectionIndex === null
                              ? 'text-white/70'
                              : 'text-gray-400'
                          }`}>
                            <Eye className="w-3 h-3 mr-1" />
                            {section.subsections.length} {t('alt bölüm', 'subsections', 'подразделения')}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Alt bölümler - tüm bölümlerde göster */}
                  {section.subsections && (
                    <div className="ml-4 space-y-1">
                      {section.subsections.map((subsection, subIndex) => (
                        <button
                          key={subsection.id}
                          onClick={() => handleSubsectionSelect(subsection, subIndex, index)}
                          className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                            currentSectionIndex === index && currentSubsectionIndex === subIndex
                              ? 'bg-gradient-to-r from-primary-400 to-primary-500 text-white shadow-md'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <div className={`w-6 h-6 rounded-md flex items-center justify-center ${
                              currentSectionIndex === index && currentSubsectionIndex === subIndex
                                ? 'bg-white/20'
                                : 'bg-primary-100'
                            }`}>
                              <BookOpen className={`w-3 h-3 ${
                                currentSectionIndex === index && currentSubsectionIndex === subIndex
                                  ? 'text-white'
                                  : 'text-primary-600'
                              }`} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-sm">{getDisplayTitle(subsection)}</h4>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}





      {/* QR Scanner Modal */}
      {isQRScannerOpen && (
        <Modal
          isOpen={isQRScannerOpen}
          onClose={() => setIsQRScannerOpen(false)}
        >
          <QRScanner
            isOpen={isQRScannerOpen}
            onScan={handleQRScanResult}
            onClose={() => setIsQRScannerOpen(false)}
          />
        </Modal>
      )}

      {/* Media Player */}
      {isMediaPlayerOpen && currentAudio && (
        <MediaPlayer
          audioUrl={currentAudio.url}
          title={currentAudio.title}
          onClose={() => {
            setIsMediaPlayerOpen(false);
            setCurrentAudio(null);
          }}
          isVisible={isMediaPlayerOpen}
          onPrevious={handlePreviousSection}
          onNext={handleNextSection}
          hasPrevious={hasPrevious()}
          hasNext={hasNext()}
        />
      )}

    </div>
  );
};

export default LocationDetail;
